import { useTranslation } from 'react-i18next'
import { Separator } from '@/components/ui/separator'
import { formatCurrency } from '@/lib/formatters'
import type { DeepOrder } from '../types'
import { calculateSubtotal, calculateTotal, calculateTotalDiscount } from '../utils'

/** Replace HTML entities in discount reason text (matching RestaurantApp behaviour) */
function formatDiscountReason(reason: string): string {
  if (!reason) return ''
  return reason.replace(/&euro/g, '€').replace(/&pound/g, '£')
}

interface PricingBreakdownProps {
  order: DeepOrder
  compact?: boolean
}

export function PricingBreakdown({ order, compact = false }: PricingBreakdownProps) {
  const { t } = useTranslation('orders')

  const subtotal = calculateSubtotal(order)
  const discount = calculateTotalDiscount(order)
  const total = calculateTotal(order)
  const hasDiscount = discount > 0.005
  const hasVoucher = order.vouchersUsed && order.vouchersUsed.length > 0
  const hasDeliveryFee = order.deliveryFee > 0
  const hasTip = order.tipsFee > 0

  if (compact) {
    return (
      <div className="font-semibold text-lg">
        {formatCurrency(total)}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
        {t('pricing.title')}
      </h4>

      <div className="space-y-2 text-sm">
        {/* Subtotal */}
        <div className="flex justify-between">
          <span className="text-muted-foreground">{t('pricing.subtotal')}</span>
          <span>{formatCurrency(subtotal)}</span>
        </div>

        {/* Discount */}
        {hasDiscount && (
          <div className="flex justify-between text-green-600">
            <span>{formatDiscountReason(order.reason) || t('pricing.discount')}</span>
            <span>-{formatCurrency(discount)}</span>
          </div>
        )}

        {/* Vouchers */}
        {hasVoucher &&
          order.vouchersUsed!.map((voucher) => (
            <div key={voucher.id} className="flex justify-between text-green-600">
              <span>{t('pricing.voucherDiscount', { code: voucher.code })}</span>
              <span>-{formatCurrency(voucher.discount)}</span>
            </div>
          ))}

        {/* Delivery fee */}
        {hasDeliveryFee && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('pricing.deliveryFee')}</span>
            <span>{formatCurrency(order.deliveryFee)}</span>
          </div>
        )}

        {/* Tip */}
        {hasTip && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('pricing.tip')}</span>
            <span>{formatCurrency(order.tipsFee)}</span>
          </div>
        )}

        <Separator className="my-2" />

        {/* Total */}
        <div className="flex justify-between font-semibold text-base">
          <span>{t('pricing.total')}</span>
          <span>{formatCurrency(total)}</span>
        </div>
      </div>
    </div>
  )
}
